import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItemText,
  ListItem,
  Switch,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCourseStageAccordionController } from '../hooks/userCourseStageAccordion';
import { UserStageProgress } from '../types/CourseStageAccordion.type';
import { Registration } from '../types/RegistrationTable.type';
import { useEffect, useState } from 'react';
import { levelAlternativeColors, levelColors } from '../utils/colorHandler';
import { EmptyValue } from './common/EmptyValue';
import { rowHoverBg, selectedRowBg } from '../utils/rowHighlight';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface AccordionProps {
  data: {
    level: string;
    stages: UserStageProgress[];
  }[];
  registration: Registration | null;
  fetchUserStageProgress: (
    subSubTypeId: number,
    registrationId: number,
  ) => Promise<void>;
  onEntryLevelUpdated: () => void;
}

const CourseStageAccordion = (props: AccordionProps) => {
  const {
    currentLevel,
    currentLevelNumber,
    handleCurrentLevel,
    handleCompleteStage,
    calculateAge,
    courseLevels,
    fetchCourseLevels,
    selectedCourseLevel,
    setSelectedCourseLevel,
    handleSetEntryLevel,

    limit,
    page,
    totalItems,
    courseLevelLogData,
    fetchLevelLogs,
    handleChangePage,
    handleChangeRowsPerPage,
    filterDate,
    setFilterDate,

    openEntryDialog,
    handleEntryDialogOpen,
    handleEntryDialogClose,
    openUncheckStageDialog,
    handleUncheckStageDialogOpen,
    handleUncheckStageDialogClose,

    highlightedRow,
    setHighlightedRow,
  } = useCourseStageAccordionController();

  const [accordionData, setAccordionData] = useState(props.data);
  const [uncheckItem, setUncheckItem] = useState<{
    level: string;
    stages: UserStageProgress[];
  } | null>(null);
  const [uncheckStage, setUncheckStage] = useState<UserStageProgress | null>(
    null,
  );

  useEffect(() => {
    handleCurrentLevel(
      props.registration?.subsubtypeId || 0,
      props.registration?.id || 0,
    );
    fetchCourseLevels(props.registration?.subsubtypeId || 0);
  }, [props.registration]);

  useEffect(() => {
    setAccordionData(props.data);
  }, [props.data]);

  useEffect(() => {
    fetchLevelLogs(props.registration?.id || 0);
  }, [props.registration, page, limit, filterDate]);

  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 overflow-y-auto pr-2">
          {accordionData.map((item, index) => (
            <>
              <Accordion key={index} className="mb-4">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    variant="h6"
                    className="text-xl !font-bold text-primary"
                  >
                    {item.level}
                    <span className="text-xs font-normal text-gray-900 dark:text-gray-200 ml-2">
                      ({item.stages.filter((stage) => stage.completed).length}/
                      {item.stages.length} stages)
                    </span>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {item.stages.map((stage, subIndex) => (
                      <ListItem key={subIndex}>
                        <ListItemText primary={stage.stageName} />
                        <Switch
                          disabled={stage.level > currentLevelNumber}
                          checked={stage.completed}
                          onChange={async () => {
                            if (stage.completed) {
                              handleUncheckStageDialogOpen();
                              setUncheckStage(stage);
                              setUncheckItem(item);
                            } else {
                              setAccordionData((prev) =>
                                prev.map((levelItem) =>
                                  levelItem.level === item.level
                                    ? {
                                        ...levelItem,
                                        stages: levelItem.stages.map((s) =>
                                          s.id === stage.id
                                            ? { ...s, completed: !s.completed }
                                            : s,
                                        ),
                                      }
                                    : levelItem,
                                ),
                              );

                              await handleCompleteStage(
                                props.registration?.id || 0,
                                stage.id,
                              );

                              await props.fetchUserStageProgress(
                                props.registration?.subsubtypeId || 0,
                                props.registration?.id || 0,
                              );

                              await handleCurrentLevel(
                                props.registration?.subsubtypeId || 0,
                                props.registration?.id || 0,
                              );

                              await fetchLevelLogs(props.registration?.id || 0);
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </>
          ))}
        </div>

        <div className="col-span-1 border rounded p-3">
          <p className="text-sm font-bold mb-3">Basic Information</p>
          <Typography variant="h6" className="font-bold mb-3 text-secondary">
            <b>User ID:</b> {props.registration?.user.id}
          </Typography>
          <Typography variant="h6" className="font-bold mb-3 text-secondary">
            <b>Name:</b> {props.registration?.user.firstName}{' '}
            {props.registration?.user.lastName}
          </Typography>
          <Typography variant="h6" className="font-bold mb-3 text-secondary">
            <b>DOB:</b>{' '}
            {new Date(props.registration?.user.dob || '').toLocaleDateString()}
            <span className="text-sm font-normal text-gray-900 dark:text-gray-200 ml-2 italic">
              ({calculateAge(props.registration?.user.dob || '')} years old)
            </span>
          </Typography>
          <Typography
            variant="h6"
            className="font-bold mb-3"
            sx={{
              color:
                { ...levelColors, ...levelAlternativeColors }[currentLevel] ||
                '#000',
            }}
          >
            <b className="text-secondary">Current Level:</b>{' '}
            {currentLevel || 'N/A'}
          </Typography>

          <Divider
            sx={{
              my: 2,
            }}
          />

          <div className="flex flex-col gap-1">
            <p className="text-sm font-bold mb-3">Quick Set Level</p>
            <p className="text-sm font-normal mb-3">
              Please select your desired entry level from the options below:
            </p>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                displayEmpty
                value={selectedCourseLevel}
                onChange={(e) => {
                  setSelectedCourseLevel(e.target.value as number);
                }}
              >
                {courseLevels.map((level) => (
                  <MenuItem key={level.level} value={level.level}>
                    {level.levelName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <button
              className="bg-primary text-white rounded p-2 mt-3"
              onClick={handleEntryDialogOpen}
            >
              Set Entry Level
            </button>
          </div>
        </div>

        <div className="col-span-3">
          <div className="flex items-center justify-between mb-4">
            <Typography variant="h6" fontWeight="bold" mb={2}>
              History
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                label="Filter by Action Date"
                value={filterDate}
                onChange={(newDate) => setFilterDate(newDate)}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                  field: {
                    clearable: true,
                    onClear: () => setFilterDate(null),
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#4da7db' }}>
                  <TableCell>
                    <Typography fontWeight="bold" color="#fff">
                      Date
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" color="#fff">
                      Time
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" color="#fff">
                      Coach
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" color="#fff">
                      Action
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courseLevelLogData.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:hover': { backgroundColor: rowHoverBg },
                      backgroundColor:
                        highlightedRow === `data_${item.id}`
                          ? selectedRowBg
                          : 'inherit',
                    }}
                    onClick={() => {
                      if (highlightedRow === `data_${item.id}`) {
                        setHighlightedRow(null);
                      } else {
                        setHighlightedRow(`data_${item.id}`);
                      }
                    }}
                  >
                    <TableCell>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(item.createdAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>{item.coach?.name}</TableCell>
                    <TableCell>{item.comment || <EmptyValue />}</TableCell>
                  </TableRow>
                ))}
                {courseLevelLogData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No log found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalItems}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>

        {/* Entry Level Dialog */}
        <Dialog open={openEntryDialog} onClose={handleEntryDialogClose}>
          <DialogTitle>Set Entry Level</DialogTitle>
          <DialogContent>
            Are you sure to set the entry level for this user?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleEntryDialogClose}
              variant="contained"
              color="error"
            >
              Close
            </Button>
            <Button
              onClick={async () => {
                const entryLevelUpdated = await handleSetEntryLevel(
                  props.registration?.subsubtypeId || 0,
                  props.registration?.id || 0,
                );

                if (!entryLevelUpdated) {
                  return;
                }

                await props.fetchUserStageProgress(
                  props.registration?.subsubtypeId || 0,
                  props.registration?.id || 0,
                );

                await handleCurrentLevel(
                  props.registration?.subsubtypeId || 0,
                  props.registration?.id || 0,
                );

                await fetchLevelLogs(props.registration?.id || 0);

                props.onEntryLevelUpdated();
                handleEntryDialogClose();
              }}
              variant="contained"
              color="primary"
              className="!text-white"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Uncheck Stage Dialog */}
        <Dialog
          open={openUncheckStageDialog}
          onClose={handleUncheckStageDialogClose}
        >
          <DialogTitle>Uncheck this stage</DialogTitle>
          <DialogContent>Do you want to uncheck this stage?</DialogContent>
          <DialogActions>
            <Button
              onClick={handleUncheckStageDialogClose}
              variant="contained"
              color="error"
            >
              Close
            </Button>
            <Button
              onClick={async () => {
                setAccordionData((prev) =>
                  prev.map((levelItem) =>
                    levelItem.level === uncheckItem?.level
                      ? {
                          ...levelItem,
                          stages: levelItem.stages.map((s) =>
                            s.id === uncheckStage?.id
                              ? { ...s, completed: !s.completed }
                              : s,
                          ),
                        }
                      : levelItem,
                  ),
                );

                await handleCompleteStage(
                  props.registration?.id || 0,
                  uncheckStage?.id || 0,
                );

                await props.fetchUserStageProgress(
                  props.registration?.subsubtypeId || 0,
                  props.registration?.id || 0,
                );

                await handleCurrentLevel(
                  props.registration?.subsubtypeId || 0,
                  props.registration?.id || 0,
                );

                await fetchLevelLogs(props.registration?.id || 0);

                setUncheckStage(null);
                setUncheckItem(null);
                handleUncheckStageDialogClose();
              }}
              variant="contained"
              color="primary"
              className="!text-white"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default CourseStageAccordion;
